import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Ruler, X, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import { FEATURED_PROJECTS, SECTORS } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectName = ({ name, className = "" }) => {
    if (!name.includes("miCare")) return <span className={className}>{name}</span>;
    const [before, after] = name.split("miCare");
    return (
        <span className={className}>
            {before}
            <span className="normal-case">mi</span>C<span className="normal-case">are</span>
            {after}
        </span>
    );
};

const Projects = () => {
    const [active, setActive] = useState("all");
    const [selected, setSelected] = useState(null);
    const [projects, setProjects] = useState(FEATURED_PROJECTS);

    useEffect(() => {
        axios.get(`${API}/projects`).then((r) => {
            if (Array.isArray(r.data) && r.data.length > 0) setProjects(r.data);
        }).catch(() => { /* fall back to static */ });
    }, []);

    const filtered = useMemo(() => {
        const sectorOrder = SECTORS.map((s) => s.slug);
        const list = active === "all"
            ? [...projects]
            : projects.filter((p) => p.sector === active);
        return list.sort((a, b) => {
            const ia = sectorOrder.indexOf(a.sector);
            const ib = sectorOrder.indexOf(b.sector);
            return ia - ib;
        });
    }, [active, projects]);

    return (
        <main data-testid="projects-page" className="bg-background">
            {/* HEADER */}
            <section className="px-6 md:px-12 lg:px-24 pt-24 md:pt-32 pb-12 border-b border-border">
                <div className="grid md:grid-cols-12 gap-8 items-end">
                    <div className="md:col-span-8">
                        <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                            Portfolio
                        </div>
                        <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.85]">
                            What we&apos;ve
                            <br /> built.
                        </h1>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <p className="text-muted-foreground max-w-sm md:ml-auto">
                            Click any project to see the full photo set and project details.
                        </p>
                    </div>
                </div>
            </section>

            {/* FILTER */}
            <section className="px-6 md:px-12 lg:px-24 py-8 border-b border-border sticky top-20 bg-background/95 backdrop-blur-sm z-30">
                <div className="flex flex-wrap gap-2">
                    <FilterChip slug="all" label="All Market Sectors" active={active} onClick={setActive} />
                    {SECTORS.map((c) => (
                        <FilterChip
                            key={c.slug}
                            slug={c.slug}
                            label={c.name}
                            active={active}
                            onClick={setActive}
                        />
                    ))}
                </div>
            </section>

            {/* GRID */}
            <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
                {filtered.length === 0 ? (
                    <div className="py-20 text-center">
                        <p className="font-mono text-sm tracking-[0.2em] uppercase text-muted-foreground">
                            No projects featured in this sector yet — check back soon.
                        </p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-background border border-border">
                        {filtered.map((p, i) => (
                            <ProjectCard
                                key={p.id}
                                project={p}
                                index={i}
                                onClick={() => setSelected(p)}
                            />
                        ))}
                    </div>
                )}

                <div className="mt-24 border-t border-border pt-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight max-w-md">
                        Your project, next on the list.
                    </div>
                    <Link
                        to="/contact"
                        data-testid="projects-cta"
                        className="group inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-4 font-bold uppercase tracking-wider hover:bg-foreground transition-colors"
                    >
                        Start a conversation
                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </div>
            </section>

            {selected && (
                <ProjectDetail project={selected} onClose={() => setSelected(null)} />
            )}
        </main>
    );
};

const ProjectCard = ({ project, index, onClick }) => {
    const [hover, setHover] = useState(false);
    const [imgIndex, setImgIndex] = useState(0);

    useEffect(() => {
        if (!hover || project.images.length < 2) return;
        const timer = setInterval(() => {
            setImgIndex((i) => (i + 1) % project.images.length);
        }, 1400);
        return () => clearInterval(timer);
    }, [hover, project.images.length]);

    useEffect(() => {
        if (!hover) setImgIndex(0);
    }, [hover]);

    const handleKey = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
        }
    };

    return (
        <article
            data-testid={`project-${project.id}`}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onFocus={() => setHover(true)}
            onBlur={() => setHover(false)}
            onClick={onClick}
            onKeyDown={handleKey}
            tabIndex={0}
            role="button"
            className="group bg-card relative overflow-hidden focus:outline-none focus:ring-2 focus:ring-accent cursor-pointer"
        >
            <div className={`relative overflow-hidden ${project.coverFit === "contain" ? "" : "aspect-[4/5] bg-foreground"}`}>
                {project.coverFit === "contain" ? (
                    <img
                        src={project.images[0]}
                        alt={`${project.name} — cover`}
                        className="block w-full h-auto"
                    />
                ) : (
                    project.images.map((src, i) => (
                        <img
                            key={src}
                            src={src}
                            alt={`${project.name} — view ${i + 1}`}
                            className={`absolute inset-0 w-full h-full transition-opacity duration-700 ease-out ${
                                project.logoFit ? "object-contain p-6 bg-background" : "object-cover"
                            } ${i === imgIndex ? "opacity-100" : "opacity-0"}`}
                        />
                    ))
                )}

                <div className="absolute top-4 left-4 bg-background px-3 py-1 font-mono text-[10px] tracking-[0.25em] uppercase text-foreground z-10">
                    {String(index + 1).padStart(2, "0")} · {project.sectorLabel}
                </div>

                {hover && project.images.length > 1 && (
                    <div className="absolute top-4 right-4 bg-background/90 px-2 py-1 font-mono text-[10px] tracking-[0.2em] text-foreground z-10">
                        {imgIndex + 1} / {project.images.length}
                    </div>
                )}

                <div
                    className={`absolute inset-0 bg-foreground/85 backdrop-blur-sm flex flex-col justify-end p-6 md:p-7 transition-opacity duration-500 z-10 ${
                        hover ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                >
                    <p className="text-background/85 text-sm leading-relaxed mb-5">
                        {project.description}
                    </p>
                    <div className="grid grid-cols-1 gap-3 pt-4 border-t border-background/20">
                        <DetailRow
                            icon={<Building2 className="w-4 h-4" />}
                            label="Partner"
                            value={project.partner}
                        />
                    </div>
                    <div className="mt-5 font-mono text-[10px] tracking-[0.3em] uppercase text-accent">
                        Click to view all photos →
                    </div>
                </div>
            </div>

            <div className="p-5 border-t border-border bg-card">
                <h3 className="font-display font-black text-xl uppercase tracking-tight leading-tight">
                    <ProjectName name={project.name} />
                </h3>
            </div>
        </article>
    );
};

const ProjectDetail = ({ project, onClose }) => {
    const [lightbox, setLightbox] = useState(null); // index of full-size photo

    // Lock body scroll while modal is open + push a history marker so browser Back closes the modal.
    // Design: X button + Escape both call history.back() when marker present, letting the popstate
    // handler invoke onClose. Push/pop stay balanced even under React.StrictMode double-invoke.
    const onCloseRef = React.useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        // Push marker only if absent (survives StrictMode double-invoke because we check real history)
        if (!window.history.state?.mwcModal) {
            window.history.pushState({ mwcModal: true }, "");
        }

        const handlePop = () => {
            onCloseRef.current?.();
        };
        window.addEventListener("popstate", handlePop);

        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("popstate", handlePop);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Route the X button and Escape through history.back() when marker present
    const requestClose = React.useCallback(() => {
        if (window.history.state?.mwcModal) {
            window.history.back();
        } else {
            onCloseRef.current?.();
        }
    }, []);

    // Keyboard controls
    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape") {
                if (lightbox !== null) setLightbox(null);
                else requestClose();
            }
            if (lightbox !== null) {
                if (e.key === "ArrowRight") {
                    setLightbox((i) => (i + 1) % project.images.length);
                }
                if (e.key === "ArrowLeft") {
                    setLightbox((i) => (i - 1 + project.images.length) % project.images.length);
                }
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [lightbox, project.images.length, requestClose]);

    return (
        <div
            data-testid="project-detail-modal"
            className="fixed inset-0 z-[100] bg-background overflow-y-auto"
        >
            {/* HEADER */}
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-md border-b border-border">
                <div className="px-6 md:px-12 lg:px-16 py-5 flex items-start justify-between gap-6">
                    <div className="flex-1 min-w-0">
                        <div className="font-mono text-[10px] sm:text-xs tracking-[0.3em] uppercase text-accent mb-1">
                            {project.sectorLabel}
                        </div>
                        <h2 className="font-display font-black text-2xl sm:text-4xl uppercase tracking-tight leading-tight truncate">
                            <ProjectName name={project.name} />
                        </h2>
                    </div>
                    <button
                        onClick={requestClose}
                        data-testid="modal-close"
                        aria-label="Close project detail"
                        className="shrink-0 p-3 bg-foreground text-background hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* CONTENT */}
            <div className="px-6 md:px-12 lg:px-16 py-10 md:py-14">
                {/* INFO BLOCK */}
                <div className="grid md:grid-cols-12 gap-8 mb-12 md:mb-16">
                    <div className="md:col-span-7">
                        <p className="text-lg md:text-xl text-foreground/85 leading-relaxed">
                            {project.description}
                        </p>
                    </div>
                    <div className="md:col-span-5">
                        <dl className="grid grid-cols-1 gap-px bg-background border border-border">
                            <InfoRow label="Sector" value={project.sectorLabel} />
                            {project.partner && project.partner !== "—" && (
                                <InfoRow label="Partner" value={project.partner} />
                            )}
                            {project.architect && project.architect !== "—" && (
                                <InfoRow label="Architect" value={project.architect} />
                            )}
                        </dl>
                    </div>
                </div>

                {/* PHOTO GRID */}
                <h3 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight mb-8 text-accent">
                    All Photos
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-background border border-border">
                    {project.images.map((src, i) => (
                        <button
                            key={src}
                            onClick={() => setLightbox(i)}
                            data-testid={`detail-photo-${i}`}
                            className="group relative aspect-[4/3] bg-foreground overflow-hidden focus:outline-none focus:ring-2 focus:ring-accent"
                        >
                            <img
                                src={src}
                                alt={`${project.name} — photo ${i + 1}`}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/20 transition-colors" />
                        </button>
                    ))}
                </div>

                {/* BOTTOM CTA */}
                <div className="mt-16 pt-10 border-t border-border flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="font-display font-black text-xl md:text-2xl uppercase tracking-tight">
                        Interested in a similar build?
                    </div>
                    <Link
                        to="/contact"
                        onClick={() => {
                            // Clear the history marker without traversing so the /contact
                            // navigation isn't undone by a queued popstate.
                            if (window.history.state?.mwcModal) {
                                window.history.replaceState({}, "");
                            }
                            onCloseRef.current?.();
                        }}
                        data-testid="detail-contact-cta"
                        className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-5 py-3 text-sm font-bold uppercase tracking-wider hover:bg-foreground transition-colors"
                    >
                        Start a conversation
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>

            {/* LIGHTBOX */}
            {lightbox !== null && (
                <Lightbox
                    project={project}
                    index={lightbox}
                    onClose={() => setLightbox(null)}
                    onPrev={() => setLightbox((i) => (i - 1 + project.images.length) % project.images.length)}
                    onNext={() => setLightbox((i) => (i + 1) % project.images.length)}
                />
            )}
        </div>
    );
};

const Lightbox = ({ project, index, onClose, onPrev, onNext }) => (
    <div
        data-testid="lightbox"
        className="fixed inset-0 z-[110] bg-foreground/95 backdrop-blur-md flex items-center justify-center"
        onClick={onClose}
    >
        <button
            onClick={(e) => {
                e.stopPropagation();
                onClose();
            }}
            aria-label="Close photo"
            data-testid="lightbox-close"
            className="absolute top-5 right-5 p-3 bg-background text-foreground hover:bg-accent hover:text-accent-foreground transition-colors z-10"
        >
            <X className="w-5 h-5" />
        </button>

        <button
            onClick={(e) => {
                e.stopPropagation();
                onPrev();
            }}
            aria-label="Previous photo"
            className="absolute left-2 md:left-5 top-1/2 -translate-y-1/2 p-3 bg-background/90 text-foreground hover:bg-accent hover:text-accent-foreground transition-colors z-10"
        >
            <ChevronLeft className="w-6 h-6" />
        </button>

        <button
            onClick={(e) => {
                e.stopPropagation();
                onNext();
            }}
            aria-label="Next photo"
            className="absolute right-2 md:right-5 top-1/2 -translate-y-1/2 p-3 bg-background/90 text-foreground hover:bg-accent hover:text-accent-foreground transition-colors z-10"
        >
            <ChevronRight className="w-6 h-6" />
        </button>

        <img
            src={project.images[index]}
            alt={`${project.name} — photo ${index + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[85vh] object-contain shadow-2xl"
        />

        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-background px-4 py-2 font-mono text-xs tracking-[0.2em] text-foreground">
            {String(index + 1).padStart(2, "0")} / {String(project.images.length).padStart(2, "0")} · {project.name}
        </div>
    </div>
);

const InfoRow = ({ label, value }) => (
    <div className="bg-card p-5 flex items-start justify-between gap-4">
        <dt className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground shrink-0">
            {label}
        </dt>
        <dd className="font-medium text-right text-foreground">{value}</dd>
    </div>
);

const DetailRow = ({ icon, label, value }) => (
    <div className="flex items-start gap-3 text-background">
        <span className="text-accent mt-0.5">{icon}</span>
        <div className="flex-1 flex items-baseline justify-between gap-3">
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-background/60">
                {label}
            </span>
            <span className="text-sm font-medium text-right">{value}</span>
        </div>
    </div>
);

const FilterChip = ({ slug, label, active, onClick }) => {
    const isActive = active === slug;
    return (
        <button
            onClick={() => onClick(slug)}
            data-testid={`filter-${slug}`}
            className={`px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] border transition-colors ${
                isActive
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background text-muted-foreground border-border hover:border-accent hover:text-foreground"
            }`}
        >
            {label}
        </button>
    );
};

export default Projects;
